import React, { useCallback, useMemo, useRef } from 'react';
import { FlexInputSpan, Form } from 'components/Form';
import { Setting, SettingDetail, SettingInfo, SettingsSectionHeading } from 'components/Setting/style';
import { Divider, InlineLink, ParagraphXSmallRegular } from 'components/Typography';
import _debounce from 'lodash/debounce';
import { StyledInputLabel, StyledTextArea, SubmitMessageButton, TextAreaWrapper } from 'components/CustomClosureMessage/style';
import { ContactAdminTooltip } from 'components/Tooltip';
import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { ErrorToast } from 'components/Toast';
import { STRINGS } from 'config';
import { saveCustomClosureMessage } from 'store/action/demandManagement';

const { PENDING, SUCCEEDED, FAILED } = STRINGS.REQUEST_STATUS;

export const CustomClosureMessageForm = props => {
  const {
    selectedPractice,
    message,
    showDefaultMessage,
    maxMessageLength,
    isGetRequestPending,
    onChangeMessage,
    isFormDisabled,
    selectedPracticeHasAdminRights,
    setSaveRequestStatus,
    featureFlag,
  } = props;

  const textareaRef = useRef();

  const onSubmit = useCallback(() => {
    if (message.length > maxMessageLength) {
      return toast(<ErrorToast message="Message is too long." />);
    }
    if (!selectedPractice) {
      return null;
    }
    setSaveRequestStatus(PENDING);
    return saveCustomClosureMessage({
      practiceId: selectedPractice?.id,
      message: message.trim(),
      showDefaultMessage: !featureFlag ? true : showDefaultMessage,
    })
      .then(isSuccess => {
        if (!isSuccess) {
          setSaveRequestStatus(FAILED);
        }
        setSaveRequestStatus(SUCCEEDED);
      });
  }, [featureFlag, maxMessageLength, message, selectedPractice, setSaveRequestStatus, showDefaultMessage]);

  const debouncedSubmit = useMemo(() => _debounce(onSubmit, 2_000, { leading: true }), [onSubmit]);

  return (
    <Setting>
      <SettingInfo>
        <SettingsSectionHeading>
          Custom message when clinical closed (admin open)
        </SettingsSectionHeading>

        <ParagraphXSmallRegular>
          This allows you to set a message for patients to see when only routine pathways are active.
          To set a custom message that always appears on your landing page, go to <InlineLink href="/other-settings">Other settings.</InlineLink>
        </ParagraphXSmallRegular>
      </SettingInfo>

      <SettingDetail>
        <Form
          action=""
          onSubmit={event => {
            event.preventDefault();
            debouncedSubmit();
          }}
          autocomplete="off"
          data-automation="CustomClosureMessageForm"
        >
          <StyledInputLabel htmlFor="CustomClosureMessageInput" state="default">
            <FlexInputSpan data-automation="CustomClosureMessageHeading" htmlFor="CustomClosureMessageInput">Message</FlexInputSpan>

            <TextAreaWrapper>
              <StyledTextArea
                charCounterId="messageCharCounter"
                maxChars={maxMessageLength}
                automationName="CustomClosureMessageInput"
                value={message}
                placeholder="Enter your custom message here (optional)"
                id="CustomClosureMessageInput"
                inputRef={textareaRef}
                multiline
                minRows={4}
                maxRows={6}
                inputProps={{
                  'aria-label': 'Set a custom closure message',
                  'aria-describedby': 'CustomClosureMessageInput-messageCharCounter',
                  readOnly: isGetRequestPending,
                }}
                fullWidth
                onChange={onChangeMessage}
                error={message.length > maxMessageLength}
                disabled={isFormDisabled}
              />

              {showDefaultMessage && (
              <>
                <Divider />

                <ParagraphXSmallRegular data-automation="DefaultClosureMessage">
                  Follow the link below for routine requests. For help with a medical problem, please try again from [time next open].
                </ParagraphXSmallRegular>
              </>
              )}
            </TextAreaWrapper>
          </StyledInputLabel>

          <SubmitMessageButton
            size="small"
            type="submit"
            data-automation="SaveCustomClosureMessageButton"
            disabled={isFormDisabled}
          >
            Save custom message
            <ContactAdminTooltip show={!selectedPracticeHasAdminRights} automationLabel="SaveCustomClosureMessageDisabledTooltip" />
          </SubmitMessageButton>
        </Form>
      </SettingDetail>
    </Setting>
  );
};
